import {useEffect} from 'react'
import Checkbox from '../../common/components/Checkbox.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import ButtonPrimary from '../../common/components/Button/ButtonPrimary.js'
import Currency from '../../common/components/Currency.js'
import {
  itemsSelector,
  poItemIDsSelector,
  productTotalSelector,
  discountAmountSelector,
  shippingAmountSelector,
  taxAmountSelector,
  grandTotalSelector,
  isPOLockedSelector,
  setPO,
} from '../../data/pos.js'
import {showEditPOItemModal} from '../PurchaseOrderListPage/Modals/EditPOItemModal.js'
import {showRemovePOItemModal} from '../PurchaseOrderListPage/Modals/RemovePOItemModal.js'
import {showPOTextFieldModal} from '../PurchaseOrderListPage/Modals/POTextFieldModal.js'
import {updatePODetailForm} from './poDetailActions.js'
import {
  poIDSelector,
  poDetailFormSelector,
  areAllItemsSelectedSelector,
  areIndeterminateItemsSelectedSelector,
} from './poDetailSelectors.js'
import Item from './Item.js'
import {dispatch, getState, useSelector} from '../../store.js'
import {hasPOFinancialsPermissionSelector} from '../../data/me.js'
import Icon from '../../common/icons/Icon.js'
import {
  DragAndDropList,
  MOVE_AFTER,
  MOVE_BEFORE,
} from '../../common/components/DragAndDrop.js'
import isEqual from 'lodash/isEqual.js'
import html from '../../common/html.js'
import {plural} from '../../common/components/Plural.js'
import debounce from 'lodash/debounce.js'
import api from '../../common/api.js'
import {buildPath} from '../../common/querystring.js'
import className from '../../common/className.js'

function onFilterMove(values, nearValue, where) {
  const {itemIDs: originalItemIDs} = poDetailFormSelector(getState())

  // get sorted items and exclude moving item
  const itemIDs = originalItemIDs.filter((item) => !values.includes(item))

  // get index of near item
  const nearIndex = itemIDs.indexOf(nearValue)

  // put moving item in it's place
  if (where === MOVE_BEFORE) {
    itemIDs.splice(nearIndex, 0, ...values)
  } else if (where === MOVE_AFTER) {
    itemIDs.splice(nearIndex + 1, 0, ...values)
  }

  if (!isEqual(itemIDs, originalItemIDs)) {
    updatePODetailForm({itemIDs})
  }
}

function moveToTop() {
  const {selectedItemIDs, itemIDs} = poDetailFormSelector(getState())

  const firstItem = itemIDs.filter((item) => !selectedItemIDs.includes(item))[0]

  if (firstItem) {
    onFilterMove(selectedItemIDs, firstItem, MOVE_BEFORE)
  }
}

function moveToBottom() {
  const {selectedItemIDs, itemIDs} = poDetailFormSelector(getState())

  const list = itemIDs.filter((item) => !selectedItemIDs.includes(item))
  const lastItem = list[list.length - 1]

  if (lastItem) {
    onFilterMove(selectedItemIDs, lastItem, MOVE_AFTER)
  }
}

function onDragStart(itemID) {
  const {selectedItemIDs} = poDetailFormSelector(getState())

  const movingSelected = selectedItemIDs.includes(itemID)
  const count = movingSelected ? selectedItemIDs.length : 1

  return {
    values: movingSelected ? selectedItemIDs : [itemID],
    dragElementFunc() {
      return movingSelected
        ? html`<div class="drag-summary">
            <strong
              >Moving ${plural(count)`${count} selected line${['s']}`}</strong
            >
          </div>`
        : null
    },
  }
}

const debounceSaveItemPositions = debounce(async () => {
  const {itemIDs} = poDetailFormSelector(getState())
  const poID = poIDSelector(getState())

  try {
    const {json: po} = await api.put(
      buildPath(['purchase_order', poID, 'sort']),
      {
        poi_ids: itemIDs,
      },
    )

    setPO(po)
  } catch (_err) {
    // nothing
  }
}, 500)

export default function ItemsPanel() {
  const poID = useSelector(poIDSelector)
  const items = useSelector((state) => itemsSelector(state, {poID}))
  const poItemIDs = useSelector((state) => poItemIDsSelector(state, {poID}))
  const form = useSelector(poDetailFormSelector)
  const areAllItemsSelected = useSelector(areAllItemsSelectedSelector)
  const areIndeterminateItemsSelected = useSelector(
    areIndeterminateItemsSelectedSelector,
  )
  const productTotal = useSelector((state) =>
    productTotalSelector(state, {poID}),
  )
  const discountAmount = useSelector((state) =>
    discountAmountSelector(state, {poID}),
  )
  const shippingAmount = useSelector((state) =>
    shippingAmountSelector(state, {poID}),
  )
  const taxAmount = useSelector((state) => taxAmountSelector(state, {poID}))
  const grandTotal = useSelector((state) => grandTotalSelector(state, {poID}))
  const isPOLocked = useSelector((state) => isPOLockedSelector(state, {poID}))
  const hasPOFinancialsPermission = useSelector(
    hasPOFinancialsPermissionSelector,
  )

  useEffect(() => {
    updatePODetailForm({itemIDs: poItemIDs})
  }, [items])

  return (
    <DragAndDropList
      onMove={onFilterMove}
      onDragStart={onDragStart}
      onDragEnd={debounceSaveItemPositions}
    >
      <div className="relative flex">
        <div
          className={className`wrap--reposition-btns-outer ${{'wrap--reposition-btns-outer--has-totals': hasPOFinancialsPermission}}`}
        >
          {form.selectedItemIDs.length > 0 && (
            <div className="wrap--reposition-btns">
              <div className="margin-bottom-1">
                <ButtonLink
                  className="btn--reposition-top no-underline"
                  onClick={() => moveToTop()}
                  title="Move selected items to top"
                >
                  <strong>↑</strong>
                </ButtonLink>
              </div>
              <div>
                <ButtonLink
                  className="btn--reposition-bottom no-underline"
                  onClick={() => moveToBottom()}
                  title="Move selected items to bottom"
                >
                  <strong>↓</strong>
                </ButtonLink>
              </div>
            </div>
          )}
        </div>
        <div className="flex-1">
          <table className="table fs-00">
            <thead>
              <tr className="fs-n0">
                <th className="table__th table__th--md th-width-1 padding-right-0">
                  <Checkbox
                    className="margin-bottom-0"
                    checked={areAllItemsSelected}
                    indeterminate={areIndeterminateItemsSelected}
                    onClick={(checked) =>
                      updatePODetailForm({
                        selectedItemIDs: checked ? form.itemIDs : [],
                      })
                    }
                    disabled={isPOLocked}
                  />
                </th>
                <th className="table__th table__th--md">Product Name/SKU</th>
                <th className="table__th table__th--md">Supplier SKU/Notes</th>
                <th className="table__th table__th--md align-right">
                  {form.selectedItemIDs.length === 0 ? (
                    'Ordered Qty'
                  ) : (
                    <ButtonLink
                      className="darker lh-inherit align-right"
                      title="Bulk Update Ordered Qty"
                      onClick={() =>
                        showEditPOItemModal(
                          poID,
                          form.selectedItemIDs,
                          'quantity',
                        )
                      }
                    >
                      Ordered Qty
                    </ButtonLink>
                  )}
                </th>
                <th className="table__th table__th--md align-right">
                  Received Qty
                </th>
                <th className="table__th table__th--md align-right">
                  Phys On Hand
                </th>
                <th className="table__th table__th--md align-right">
                  Total Committed
                </th>
                <th className="table__th table__th--md align-right">
                  Total PO Reordered
                </th>
                {hasPOFinancialsPermission && (
                  <th className="table__th table__th--md align-right">
                    {form.selectedItemIDs.length === 0 ? (
                      'Supplier Unit Cost'
                    ) : (
                      <ButtonLink
                        className="darker lh-inherit align-right"
                        title="Bulk Update Supplier Unit Cost"
                        onClick={() =>
                          showEditPOItemModal(
                            poID,
                            form.selectedItemIDs,
                            'unit_price',
                          )
                        }
                      >
                        Supplier Unit Cost
                      </ButtonLink>
                    )}
                  </th>
                )}
                <th className="table__th table__th--md">Lead Time</th>
                {hasPOFinancialsPermission && (
                  <>
                    <th className="table__th table__th--md align-right">
                      {form.selectedItemIDs.length === 0 ? (
                        'Discount'
                      ) : (
                        <ButtonLink
                          className="darker lh-inherit align-right"
                          title="Bulk Update Discount"
                          onClick={() =>
                            showEditPOItemModal(
                              poID,
                              form.selectedItemIDs,
                              'discount_amount',
                            )
                          }
                        >
                          Discount
                        </ButtonLink>
                      )}
                    </th>
                    <th className="table__th table__th--md align-right">
                      Total
                    </th>
                  </>
                )}
                <th className="table__th table__th--md align-right">
                  {form.selectedItemIDs.length === 0 ? (
                    ''
                  ) : (
                    <ButtonLink
                      className="no-underline"
                      title="Remove supplier"
                      onClick={() =>
                        dispatch(
                          showRemovePOItemModal(poID, form.selectedItemIDs),
                        )
                      }
                    >
                      <Icon name="trash" className="fs-00" />
                    </ButtonLink>
                  )}
                </th>
              </tr>
            </thead>
            <tbody className="table__tbody table__tbody--lines">
              {form.itemIDs.map((itemID, index) => (
                <Item
                  key={itemID}
                  index={index}
                  poID={poID}
                  poItemID={itemID}
                  form={form}
                  isPOLocked={isPOLocked}
                  hasPOFinancialsPermission={hasPOFinancialsPermission}
                />
              ))}
              {!isPOLocked && (
                <tr>
                  <td
                    className="table__td table__td--product-search"
                    colSpan="13"
                  >
                    <ButtonPrimary
                      isOutlined
                      size="sm"
                      onClick={() => showEditPOItemModal(poID)}
                      autoFocus={form.afterAddingProduct}
                    >
                      Add a Product
                    </ButtonPrimary>
                  </td>
                </tr>
              )}
            </tbody>
            {hasPOFinancialsPermission && (
              <tbody className="table__tbody table__tbody--totals">
                <tr>
                  <td className="table__td fs-00 align-right" colSpan="11">
                    <strong>Product Total</strong>
                  </td>
                  <td className="table__td fs-00" colSpan="2">
                    <strong>
                      <Currency value={productTotal} />
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td
                    className="table__td fs-00 align-right v-align-middle"
                    colSpan="11"
                  >
                    <strong>Discount</strong>
                  </td>
                  <td className="table__td" colSpan="2">
                    <ButtonLink
                      className="fs-00 v-align-base darker"
                      onClick={() =>
                        dispatch(
                          showPOTextFieldModal({
                            poID,
                            value: discountAmount,
                            display: 'Discount Amount',
                            apiProp: 'discount_amount',
                            inputType: 'currency',
                            isRequired: true,
                          }),
                        )
                      }
                      disabled={isPOLocked}
                    >
                      <Currency value={discountAmount} />
                    </ButtonLink>
                  </td>
                </tr>
                <tr>
                  <td
                    className="table__td fs-00 align-right v-align-middle"
                    colSpan="11"
                  >
                    <strong>Shipping and Handling</strong>
                  </td>
                  <td className="table__td" colSpan="2">
                    <ButtonLink
                      className="fs-00 v-align-base darker"
                      onClick={() =>
                        dispatch(
                          showPOTextFieldModal({
                            poID,
                            value: shippingAmount,
                            display: 'Shipping Amount',
                            apiProp: 'shipping_amount',
                            inputType: 'currency',
                            isRequired: true,
                          }),
                        )
                      }
                      disabled={isPOLocked}
                    >
                      <Currency value={shippingAmount} />
                    </ButtonLink>
                  </td>
                </tr>
                <tr>
                  <td
                    className="table__td fs-00 align-right v-align-middle"
                    colSpan="11"
                  >
                    <strong>Tax</strong>
                  </td>
                  <td className="table__td" colSpan="2">
                    <ButtonLink
                      className="fs-00 v-align-base darker"
                      onClick={() =>
                        dispatch(
                          showPOTextFieldModal({
                            poID,
                            value: taxAmount,
                            display: 'Tax Amount',
                            apiProp: 'tax_amount',
                            inputType: 'currency',
                            isRequired: true,
                          }),
                        )
                      }
                      disabled={isPOLocked}
                    >
                      <Currency value={taxAmount} />
                    </ButtonLink>
                  </td>
                </tr>
                <tr>
                  <td
                    className="table__td table__td--grand-total fs-00 align-right"
                    colSpan="11"
                  >
                    <strong>Grand Total</strong>
                  </td>
                  <td
                    className="table__td table__td--grand-total fs-00"
                    colSpan="2"
                  >
                    <strong>
                      <Currency value={grandTotal} />
                    </strong>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>
    </DragAndDropList>
  )
}
